import type {OverlayPanel} from "primereact/overlaypanel";
import {type MouseEvent, useRef} from "react";
import {useMatch, useNavigate} from "react-router-dom";

import {useUnloadValuation} from "../hooks/valuationLoadHooks";
import logoLight from "../images/logo_value_marktdaten.png";
import logoDark from "../images/logo_value_marktdaten_dark.png";
import {selectAddressAmbiguousData, selectAddressData} from "../redux/addressSlice";
import {selectIsSnapshot} from "../redux/persistenceSlice";
import {useRevaSelector} from "../redux/revaHooks";
import {selectDarkTheme} from "../redux/themeSlice";
import {handlePreventDefault} from "../util/events";
import AddressElement from "./header/AddressElement";
import ObjectElement from "./header/ObjectElement";
import PurposeElement from "./header/PurposeElement";
import EmailVerificationNotice from "./widgets/EmailVerificationNotice";
import HeaderUserMenu from "./widgets/HeaderUserMenu";
import {ResetSessionPopup} from "./widgets/ResetSessionPopup";

/**
 * App Header.
 */
function Header() {
    const navigate = useNavigate();
    const unloadValuation = useUnloadValuation();

    const isDarkTheme = useRevaSelector(selectDarkTheme);

    const addressData = useRevaSelector(selectAddressData);
    const ambiguousAddresses = useRevaSelector(selectAddressAmbiguousData);
    const hasAddressData = !!addressData || !!ambiguousAddresses;
    const isSnapshot = useRevaSelector(selectIsSnapshot);

    const resetConfirmRef = useRef<OverlayPanel>(null);

    const matchPathAddress = useMatch("/address/*");

    const onLogoClick = (event: MouseEvent) => {
        if (!hasAddressData) {
            return;
        }
        if (isSnapshot) {
            // A frozen snapshot has no unsaved session state worth confirming — go straight to the
            // start page's "Historie" tab, which is now its own route.
            void unloadValuation("/start/history");
            return;
        }
        if (resetConfirmRef.current) {
            resetConfirmRef.current.toggle(event);
        }
    };

    const onLogoutCallback = () => {
        // Navigate to initial view (with no selected address)
        void navigate("/");
    };

    return (
        <header className="AppHeader grow-0">
            <div className="flex p-0 bg-surface-50 dark:bg-surface-950">
                <div className="AppHeaderLogo grow-0 shrink-0 px-4 py-6 self-center flex justify-center w-80">
                    <a
                        href={hasAddressData ? "/" : undefined}
                        onClick={handlePreventDefault(onLogoClick)}
                        style={{cursor: hasAddressData ? "pointer" : "default"}}
                    >
                        <img
                            src={isDarkTheme ? logoDark : logoLight}
                            className={"App-logo m-auto"}
                            height="74"
                            alt="LOGO"
                        />
                    </a>
                </div>
                <div className="AppHeaderTitle grow px-4 py-1 mt-2 self-center">
                    <span className="text-3xl">Real Estate Valuation Assistant</span>
                    {/* #19481: der Research-Chip steht mittig im freien Bereich rechts neben Adresse/Objektart/ID
                        statt darunter, die Header-Höhe bleibt wie bei einer Bewertung; ohne Research-Abfrage
                        rendert er nichts. */}
                    <div className="AppHeaderInfoRow flex items-center gap-6 min-w-0">
                        <div className="AppHeaderInfo min-w-0">
                            <AddressElement addressData={addressData} matchPathAddress={!!matchPathAddress} />
                            <ObjectElement matchPathAddress={!!matchPathAddress} />
                        </div>
                        <PurposeElement />
                    </div>
                </div>
                <div className="AppHeaderUser relative grow-0 shrink-0 p-4 self-center">
                    <HeaderUserMenu onLogoutCallback={onLogoutCallback} />
                    <EmailVerificationNotice />
                </div>
                <ResetSessionPopup ref={resetConfirmRef} />
            </div>
        </header>
    );
}

export default Header;
